import React, { useCallback, useState } from "react";
import { Send, Sparkles, X } from "lucide-react";
import { Spinner } from "../../../../ui/spinner";
import { Badge } from "../../../../ui/badge";
import { Button } from "../../../../eds";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { useShowAICoPilot } from "../../../../../hooks/useShowAICoPilot";
import { getEnvironmentProps } from "../../../../../utils/environment";
import { dmnIdentity } from "../../../../../utils/dmnIdentity";
import { parseOptimizationQueryFull } from "../../../../../services/ai/AiQueryService";
import {
  AiFullResponse,
  ProcessedScanResult,
  ScanConfiguration
} from "../SpaceScanner.types";

const getLocalDateString = (
  date: Date | string | undefined
): string | undefined => {
  if (date instanceof Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  if (typeof date === "string") return date;
  return undefined;
};

const QUICK_PROMPTS = [
  "Optimise for maximum capacity tonight",
  "Arrange tables for best service flow",
  "Balanced layout for tonight",
  "Analyse my setup for improvements"
];

interface ResponseCardProps {
  children: React.ReactNode;
  onClear: () => void;
}

const ResponseCard = ({ children, onClear }: ResponseCardProps) => (
  <div className="mt-3 rounded-md border border-border bg-card p-3">
    {children}
    <div className="mt-3 flex justify-end">
      <button
        type="button"
        className="flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
        onClick={onClear}
        aria-label="Clear response"
      >
        <X className="size-3" aria-hidden="true" />
        Clear
      </button>
    </div>
  </div>
);

interface CoPilotChatProps {
  onApplyAndScan: (config: Partial<ScanConfiguration>) => void;
  onScanResultsReady?: (
    results: ProcessedScanResult[],
    config: ScanConfiguration
  ) => void;
  currentConfig: ScanConfiguration;
  autoScan?: boolean;
}

export const CoPilotChat = ({
  onApplyAndScan,
  onScanResultsReady,
  currentConfig,
  autoScan = true
}: CoPilotChatProps): React.ReactElement | null => {
  const { venueConfig, date } = useDiaryContext();
  const isFeatureEnabled = useShowAICoPilot();

  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState<AiFullResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submitQuery = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;

      setQuery(trimmed);
      setIsLoading(true);
      setError(null);
      setAiResponse(null);

      try {
        const token = await dmnIdentity.getAccessToken();
        const env = getEnvironmentProps(token);
        const baseUrl = env?.CORE_API_BASE_URL ?? "";
        const venueId = venueConfig?._id ?? "";

        const dateString = getLocalDateString(date);

        const response = await parseOptimizationQueryFull({
          venueId,
          query: trimmed,
          baseUrl,
          token,
          currentConfig,
          date: dateString,
          autoScan
        });

        setAiResponse(response);
      } catch {
        setError("Something went wrong. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, venueConfig, currentConfig, date, autoScan]
  );

  const handleSubmit = useCallback(async () => {
    const trimmed = query.trim();
    if (!trimmed || isLoading) return;

    await submitQuery(trimmed);
  }, [query, isLoading, submitQuery]);

  const handleQuickPrompt = useCallback(
    (prompt: string) => {
      submitQuery(prompt);
    },
    [submitQuery]
  );

  const handleApply = useCallback(() => {
    if (!aiResponse || aiResponse.intent !== "configure") return;

    if (
      aiResponse.scanResults?.results &&
      aiResponse.scanResults?.summary &&
      onScanResultsReady
    ) {
      const config: ScanConfiguration = {
        optimizationStrategy: aiResponse.strategy,
        allowOverCapacity: aiResponse.allowOverCapacity,
        searchSeatedCapacity: aiResponse.searchSeatedCapacity,
        areasToEmpty: aiResponse.areasToEmpty
      };
      onScanResultsReady(aiResponse.scanResults.results, config);
    } else {
      if (
        process.env.NODE_ENV !== "production" &&
        aiResponse.scanResults?.results &&
        !onScanResultsReady
      ) {
        console.warn(
          "[CoPilotChat] scanResults returned but onScanResultsReady not provided — falling back to onApplyAndScan"
        );
      }
      const config: Partial<ScanConfiguration> = {
        optimizationStrategy: aiResponse.strategy,
        allowOverCapacity: aiResponse.allowOverCapacity,
        searchSeatedCapacity: aiResponse.searchSeatedCapacity
      };
      onApplyAndScan(config);
    }

    setAiResponse(null);
    setQuery("");
  }, [aiResponse, onApplyAndScan, onScanResultsReady]);

  const handleClear = useCallback(() => {
    setQuery("");
    setAiResponse(null);
    setError(null);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  if (!isFeatureEnabled) return null;

  return (
    <section
      className="my-3 rounded-lg border border-border p-4"
      aria-label="AI Assistant"
    >
      <div className="mb-3 flex items-center gap-2">
        <Sparkles
          className="size-4 flex-shrink-0 text-[var(--primaryButtonSolid)]"
          aria-hidden="true"
        />
        <span className="text-sm font-semibold text-foreground">
          AI Assistant
        </span>
        <Badge
          variant="secondary"
          className="rounded-full px-2 py-0 text-[10px] font-medium"
        >
          Beta
        </Badge>
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          className="flex-1 rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder="Ask AI Assistant, e.g., 'Optimise for maximum capacity tonight'"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          aria-label="AI Assistant query"
        />
        <button
          type="button"
          className="flex size-9 flex-shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
          onClick={handleSubmit}
          disabled={isLoading || !query.trim()}
          aria-label="Send query"
        >
          <Send className="size-4" aria-hidden="true" />
        </button>
      </div>

      {!aiResponse && !isLoading && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {QUICK_PROMPTS.map((prompt) => (
            <button
              key={prompt}
              type="button"
              className="rounded-full border border-border bg-card px-2.5 py-1 text-[11px] leading-tight text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              onClick={() => handleQuickPrompt(prompt)}
              disabled={isLoading}
            >
              {prompt}
            </button>
          ))}
        </div>
      )}

      {isLoading && (
        <div className="mt-3 flex items-center gap-2">
          <Spinner className="size-4" />
          <span className="text-xs text-muted-foreground">
            Analysing your request...
          </span>
        </div>
      )}

      {error && (
        <p className="mt-2 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}

      {aiResponse && aiResponse.intent === "configure" && (
        <ResponseCard onClear={handleClear}>
          <p className="text-sm text-foreground">{aiResponse.explanation}</p>
          <div className="mt-3 flex gap-2">
            <Button
              className="min-h-[36px] flex-1 text-xs"
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </ResponseCard>
      )}

      {aiResponse && aiResponse.intent === "explain" && (
        <ResponseCard onClear={handleClear}>
          <p className="text-sm text-foreground">{aiResponse.explanation}</p>
        </ResponseCard>
      )}

      {aiResponse && aiResponse.intent === "compare" && (
        <ResponseCard onClear={handleClear}>
          <p className="text-sm text-foreground">{aiResponse.explanation}</p>
          <p className="mt-2 text-xs text-muted-foreground">
            A detailed comparison is available based on your current
            configuration.
          </p>
        </ResponseCard>
      )}

      {aiResponse && aiResponse.intent === "out_of_scope" && (
        <ResponseCard onClear={handleClear}>
          <p className="text-sm text-foreground">
            {aiResponse.explanation ||
              "I can help with Space Optimiser queries. Try asking about capacity, spacing, or table arrangements."}
          </p>
        </ResponseCard>
      )}

      {aiResponse && aiResponse.intent === "adjust" && (
        <ResponseCard onClear={handleClear}>
          <p className="text-sm font-medium text-foreground">
            {aiResponse.display}
          </p>
          {aiResponse.explanation && (
            <p className="mt-1 text-xs text-muted-foreground">
              {aiResponse.explanation}
            </p>
          )}
        </ResponseCard>
      )}

      {aiResponse && aiResponse.intent === "advise" && (
        <ResponseCard onClear={handleClear}>
          <p className="text-sm text-foreground">{aiResponse.explanation}</p>
          {aiResponse.recommendations?.length > 0 && (
            <ul className="mt-2 space-y-2">
              {aiResponse.recommendations.map((rec, i) => (
                <li
                  key={`rec-${rec.category}-${rec.message.slice(0, 20)}-${i}`}
                  className="rounded-md border border-border p-2.5 text-xs"
                >
                  <span className="font-medium text-foreground">
                    {rec.message}
                  </span>
                  {rec.impact && (
                    <span className="mt-1 block text-muted-foreground">
                      {rec.impact}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </ResponseCard>
      )}
    </section>
  );
};
